import { useEffect, useMemo, useState, type PointerEvent } from "react";
import { ArrowDown2, InfoCircle } from "iconsax-reactjs";
import { BodyText, Button, Card, CardContent, HeadingText } from "../../../shared/ui";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../shared/ui/tooltip";
import {
  COUNTRY_DISTRIBUTION_STATS,
  KYB_APPROVAL_SUMMARY,
  KYB_REGISTRATION_SUMMARY,
  KYB_STATUS_STATS,
} from "../constants/stats";
import { KybStatsSummary } from "../types";

type DonutSegment = {
  label: string;
  color: string;
  value: number;
};

const DonutChart = ({
  segments,
  size = 126,
  innerSize = 58,
  usePointerTooltip = false,
}: {
  segments: DonutSegment[];
  size?: number;
  innerSize?: number;
  usePointerTooltip?: boolean;
}) => {
  const [isAnimated, setIsAnimated] = useState(false);
  const [activeSegmentLabel, setActiveSegmentLabel] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setIsAnimated(true);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, []);

  const strokeWidth = Math.max((size - innerSize) / 2, 12);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  const computedSegments = useMemo(() => {
    if (total <= 0) return [];

    let accumulatedOffset = 0;

    return segments.map((segment) => {
      const startOffset = accumulatedOffset;
      const ratio = segment.value / total;
      const segmentLength = circumference * ratio;
      const segmentData = {
        ...segment,
        startOffset,
        endOffset: startOffset + segmentLength,
        segmentLength,
        dashOffset: -accumulatedOffset,
      };

      accumulatedOffset += segmentLength;

      return segmentData;
    });
  }, [circumference, segments, total]);

  const activeSegment = activeSegmentLabel
    ? computedSegments.find((segment) => segment.label === activeSegmentLabel)
    : null;

  const updateActiveSegmentFromPointer = (
    event: PointerEvent<HTMLElement>,
  ) => {
    if (!usePointerTooltip || computedSegments.length === 0) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = event.clientX - centerX;
    const deltaY = event.clientY - centerY;
    const angleFromTop =
      (Math.atan2(deltaY, deltaX) * 180) / Math.PI + 90;
    const normalizedAngle = (angleFromTop + 360) % 360;
    const pointerOffset = (normalizedAngle / 360) * circumference;
    const nextActiveSegment =
      computedSegments.find(
        (segment) =>
          pointerOffset >= segment.startOffset &&
          pointerOffset < segment.endOffset,
      ) ?? computedSegments[computedSegments.length - 1];

    setActiveSegmentLabel(nextActiveSegment.label);
  };

  const chart = (
    <div className="relative shrink-0">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={`transition-all duration-700 ease-out ${
          isAnimated ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
        aria-label="Chart breakdown"
        role="img"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#F1F3FC"
          strokeWidth={strokeWidth}
        />

        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {computedSegments.map((segment) =>
            usePointerTooltip ? (
              <circle
                key={segment.label}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={strokeWidth}
                strokeLinecap="butt"
                strokeDasharray={
                  isAnimated
                    ? `${segment.segmentLength} ${circumference}`
                    : `0 ${circumference}`
                }
                strokeDashoffset={segment.dashOffset}
                style={{
                  transition:
                    "stroke-dasharray 900ms cubic-bezier(0.22, 1, 0.36, 1)",
                }}
              >
                <title>{`${segment.label}: ${segment.value}`}</title>
              </circle>
            ) : (
              <Tooltip key={segment.label}>
                <TooltipTrigger asChild>
                  <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={segment.color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="butt"
                    strokeDasharray={
                      isAnimated
                        ? `${segment.segmentLength} ${circumference}`
                        : `0 ${circumference}`
                    }
                    strokeDashoffset={segment.dashOffset}
                    style={{
                      transition:
                        "stroke-dasharray 900ms cubic-bezier(0.22, 1, 0.36, 1)",
                    }}
                  >
                    <title>{`${segment.label}: ${segment.value}`}</title>
                  </circle>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  sideOffset={10}
                  className="rounded-[10px] bg-[#161827] px-3 py-2 text-[12px] font-[450] text-white"
                >
                  {segment.label}: {segment.value}
                </TooltipContent>
              </Tooltip>
            ),
          )}
        </g>
      </svg>

      <div
        className="absolute left-1/2 top-1/2 rounded-full bg-white"
        style={{
          width: innerSize,
          height: innerSize,
          transform: "translate(-50%, -50%)",
        }}
      />
    </div>
  );

  if (!usePointerTooltip) return chart;

  return (
    <Tooltip
      open={Boolean(activeSegment)}
      onOpenChange={(open) => {
        if (!open) setActiveSegmentLabel(null);
      }}
    >
      <TooltipTrigger asChild>
        <div
          className="relative shrink-0"
          onPointerEnter={updateActiveSegmentFromPointer}
          onPointerMove={updateActiveSegmentFromPointer}
          onPointerLeave={() => setActiveSegmentLabel(null)}
          onFocus={() =>
            setActiveSegmentLabel(computedSegments[0]?.label ?? null)
          }
          onBlur={() => setActiveSegmentLabel(null)}
          tabIndex={0}
        >
          {chart}
        </div>
      </TooltipTrigger>
      {activeSegment ? (
        <TooltipContent
          side="top"
          sideOffset={10}
          className="rounded-[10px] bg-[#161827] px-3 py-2 text-[12px] font-[450] text-white"
        >
          {activeSegment.label}: {activeSegment.value}
        </TooltipContent>
      ) : null}
    </Tooltip>
  );
};

const StatLegend = ({
  items,
}: {
  items: Array<{ label: string; color: string; value?: number }>;
}) => (
  <div className="flex flex-wrap gap-x-7 gap-y-3">
    {items.map((item) => (
      <Tooltip key={item.label}>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 aku-label-regular text-[#414562]">
            <span
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: item.color }}
              aria-hidden="true"
            />
            <span>{item.label}</span>
          </div>
        </TooltipTrigger>
        {typeof item.value === "number" ? (
          <TooltipContent
            side="top"
            sideOffset={8}
            className="rounded-[10px] bg-[#161827] px-3 py-2 text-[12px] font-[450] text-white"
          >
            {item.label}: {item.value}
          </TooltipContent>
        ) : null}
      </Tooltip>
    ))}
  </div>
);

type KybComplianceStatsSectionProps = {
  stats?: KybStatsSummary | null;
  error?: string | null;
  onRetry?: () => void;
};

const formatCount = (value: number) => value.toLocaleString("en-US");

const KybComplianceStatsSection = ({
  stats,
  error,
  onRetry,
}: KybComplianceStatsSectionProps) => {
  const cardTitleClassName = "aku-heading-h5-medium text-[#161827]";
  const cardValueClassName = "aku-heading-h2-medium text-[#383B56]";
  const cardShellClassName =
    "flex h-full flex-col gap-6 p-6 lg:flex-row lg:items-start lg:justify-between";
  const cardTextColumnClassName = "min-w-0 flex-1 lg:max-w-[calc(100%-158px)]";
  const cardVisualColumnClassName =
    "flex min-h-[126px] items-center justify-center lg:w-[126px] lg:flex-none lg:justify-end";
  const registrationSummary = {
    total: formatCount(stats?.totalRegistrations ?? Number(KYB_REGISTRATION_SUMMARY.total.replace(/,/g, ""))),
    growth: stats?.registrationsGrowth ?? KYB_REGISTRATION_SUMMARY.growth,
    growthLabel:
      stats?.registrationsGrowthLabel ?? KYB_REGISTRATION_SUMMARY.growthLabel,
  };
  const approvalSummary = {
    averageApprovalTime:
      stats?.averageApprovalTime ?? KYB_APPROVAL_SUMMARY.averageApprovalTime,
    averageProcessingTime:
      stats?.averageProcessingTime ?? KYB_APPROVAL_SUMMARY.averageProcessingTime,
  };
  const statusStats = stats?.statusStats?.length
    ? stats.statusStats
    : KYB_STATUS_STATS;
  const countryDistribution = stats?.countryDistribution?.length
    ? stats.countryDistribution
    : COUNTRY_DISTRIBUTION_STATS;

  return (
    <TooltipProvider delayDuration={0}>
      <section className="mt-4 w-full">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-2 py-0.5 text-[16px] font-[400] tracking-[-0.4px] text-[#383B56]"
          >
            <BodyText className="text-[16px] text-[#383B56]">Filter:</BodyText>
            <span className="font-[500] text-[#161827]">All Time</span>
            <ArrowDown2 size={18} className="text-[#737AA8]" />
          </button>

          {error ? (
            <div className="flex items-center gap-3">
              <BodyText className="text-[13px] text-[#C24E4E]">{error}</BodyText>
              {onRetry ? (
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 rounded-[10px] px-4 text-[13px] font-[500]"
                  onClick={onRetry}
                >
                  Retry
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="scrollbar-hide mt-4 flex gap-4 overflow-x-auto pb-2">
          <Card className="w-[520px] shrink-0 overflow-hidden rounded-[16px] border border-[#E1E6FF] shadow-none min-[1562px]:w-auto min-[1562px]:flex-1">
            <CardContent className={cardShellClassName}>
              <div className={cardTextColumnClassName}>
                <div>
                  <HeadingText className={cardTitleClassName}>
                    Total registrations
                  </HeadingText>
                  <div className="mt-4 flex flex-wrap items-end gap-2">
                    <span className={cardValueClassName}>
                      {registrationSummary.total}
                    </span>
                    <div className="mb-1 flex items-center gap-2">
                      <span className="aku-label-medium font-[500] text-[#18A957]">
                        {registrationSummary.growth}
                      </span>
                      <BodyText className="aku-label-regular text-[#4C5173]">
                        {registrationSummary.growthLabel}
                      </BodyText>
                    </div>
                  </div>
                </div>
                <div className="mt-6">
                  <StatLegend items={statusStats} />
                </div>
              </div>
              <div className={cardVisualColumnClassName}>
                <DonutChart segments={statusStats} />
              </div>
            </CardContent>
          </Card>

          <Card className="w-[520px] shrink-0 overflow-hidden rounded-[16px] border border-[#E1E6FF] shadow-none min-[1562px]:w-auto min-[1562px]:flex-1">
            <CardContent className="h-full min-h-[193px] p-6">
              <div className="flex h-full min-h-[145px] flex-col justify-between">
                <div className="flex items-start gap-2">
                  <HeadingText className={cardTitleClassName}>
                    Average approval time
                  </HeadingText>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <InfoCircle size={20} className="text-[#8A91B5]" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      sideOffset={8}
                      className="rounded-[10px] bg-[#161827] px-3 py-2 text-[12px] font-[450] text-white"
                    >
                      Average time from submission to approval.
                    </TooltipContent>
                  </Tooltip>
                </div>

                <p className={cardValueClassName}>
                  {approvalSummary.averageApprovalTime}
                </p>

                <BodyText className="text-[16px] text-[#4C5173]">
                  {approvalSummary.averageProcessingTime}
                </BodyText>
              </div>
            </CardContent>
          </Card>

          <Card className="w-[520px] shrink-0 overflow-hidden rounded-[16px] border border-[#E1E6FF] shadow-none min-[1562px]:w-auto min-[1562px]:flex-1">
            <CardContent className="flex h-full min-h-[193px] flex-col gap-6 p-6 lg:flex-row lg:justify-between">
              <div className="flex min-w-0 flex-1 flex-col justify-between gap-8 lg:max-w-[calc(100%-158px)]">
                <HeadingText className={cardTitleClassName}>
                  Country distribution
                </HeadingText>
                <div>
                  <StatLegend items={countryDistribution.slice(0, 3)} />
                </div>
              </div>
              <div className="flex min-h-[145px] items-center justify-center lg:w-[126px] lg:flex-none lg:justify-end">
                <DonutChart
                  segments={countryDistribution}
                  size={126}
                  innerSize={58}
                  usePointerTooltip
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </TooltipProvider>
  );
};

export default KybComplianceStatsSection;
